import { axiosInstance } from "../config/axiosInstance";
import {
  GetNurseProcessListRequestDto,
  CreateNurseCareGroupRequestDto,
  UpdateNurseCareGroupRequestDto,
  CreateNurseCarePlanRequestDto,
  UpdateNurseCarePlanRequestDto,
  CreateNurseCarePlanDetailRequestDto,
  UpdateNurseCarePlanDetailRequestDto,
  CreatePatientNurseCarePlanRequestDto,
  UpdatePatientNurseCareDetailRequestDto,
  UpdatePatientNurseCareDetailPeriodRequestDto,
  UpdatePatientNurseCareRequestDto,
  GetAllNurseCaresParamsDto,
  GetAllNurseCarePlansByNcgIdParamsDto,
  GetAllNurseCarePlanDetailsByNcpIdParamsDto,
  DeleteNurseCareGroupPathParams,
  UpdateNurseCareGroupPathParams,
  DeleteNurseCarePlanPathParams,
  UpdateNurseCarePlanPathParams,
  DeleteNurseCarePlanDetailPathParams,
  UpdateNurseCarePlanDetailPathParams,
  GetPatientInfoPathParams,
  GetPatientChartSummaryParamsDto,
  DeletePatientNurseCarePlanPathParams,
  DeletePatientNurseCarePlanDetailPathParams,
  GetPatientNurseCarePlanParamsDto,
  GetAllNurseCarePlanDetailsByIdParamsDto,
  CreateFavoriteNurseCareGroupRequestDto,
  CreateFavoriteNurseCarePlanRequestDto,
  DeleteFavoriteNurseCareGroupPathParams,
  DeleteFavoriteNurseCarePlanPathParams,
  GetFavoriteNurseCareGroupsByUserIdParamsDto,
  GetFavoriteNurseCarePlansByUserIdParamsDto,
  GetPatientNoteParamsDto,
  UpsertPatientNoteRequestDto,
  GetVisitTypesResponseDto,
  UpdatePatientVisitTypeRequestDto,
  AuthenForPatientRequestDto,
  VerifyOrderRequestDto,
  ApproveSendBackRequestDto,
  OrderAppDateChangeRequestDto,
  SetStatusOtherTreatmentsRequestDto,
  CancelOrderTerminationRequestDto,
  NotApplicableRequestDto,
  ApplyOrderRequestDto,
  PatientChartSummaryData,
  PatientChartSummaryResponseDto,
} from "./nurseServiceDtos";

const API_URL = process.env.REACT_APP_API_URL;

const emptyPatientChartSummary: PatientChartSummaryData = {
  lengthofstaydays: null,
  activemedicationcount: null,
  panicvaluecount: null,
  allergycount: null,
  fluidbalance: null,
  painscale: null,
  bloodsugartrackingvalue: null,
  nrsassessment: null,
};

const parsePatientChartSummaryData = (
  raw: PatientChartSummaryResponseDto["data"]
): PatientChartSummaryData => {
  if (!raw) {
    return { ...emptyPatientChartSummary };
  }

  if (typeof raw === "object") {
    return { ...emptyPatientChartSummary, ...raw };
  }

  const normalized = String(raw).trim();
  if (!normalized) {
    return { ...emptyPatientChartSummary };
  }

  try {
    const jsonReady = normalized
      .replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":')
      .replace(/'/g, '"');

    const parsed = JSON.parse(jsonReady);
    return { ...emptyPatientChartSummary, ...parsed };
  } catch (error) {
    console.error("Error parsing patient chart summary:", error);
    return { ...emptyPatientChartSummary };
  }
};

export const nurseService = {
  // /api/Nurse
  nurse: {
    getNurseProcessList: async <T>(data: GetNurseProcessListRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Nurse/GetNurseProcessList`, data);
        return response.data;
      } catch (error) {
        console.error("Error fetching nurse process list:", error);
        throw error;
      }
    },
    getPatientNote: async <T>(params: GetPatientNoteParamsDto): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/Nurse/GetPatientNote`, { params });
        return response.data;
      } catch (error) {
        console.error("Error fetching patient note:", error);
        throw error;
      }
    },
    upsertPatientNote: async <T>(data: UpsertPatientNoteRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Nurse/UpsertPatientNote`, data);
        return response.data;
      } catch (error) {
        console.error("Error upserting patient note:", error);
        throw error;
      }
    },
    getVisitTypes: async <T = GetVisitTypesResponseDto>(): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/Nurse/GetVisitTypes`);
        return response.data;
      } catch (error) {
        console.error("Error fetching visit types:", error);
        throw error;
      }
    },
    updatePatientVisitType: async <T>(data: UpdatePatientVisitTypeRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Nurse/UpdatePatientVisitType`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating patient visit type:", error);
        throw error;
      }
    },
  },

  // /api/NurseCare
  nurseCare: {
    getAllNurseCares: async <T>(params?: GetAllNurseCaresParamsDto): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/NurseCare/getAllNurseCares`, { params });
        return response.data;
      } catch (error) {
        console.error("Error fetching all nurse cares:", error);
        throw error;
      }
    },
    getAllNurseCareGroups: async <T>(params?: GetAllNurseCaresParamsDto): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/NurseCare/getAllNurseCareGroups`, { params });
        return response.data;
      } catch (error) {
        console.error("Error fetching all nurse cares:", error);
        throw error;
      }
    },
    getNurseCareGroupById: async <T>(id: number): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/NurseCare/getNurseCareGroupById/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching nurse care group by ID:", error);
        throw error;
      }
    },
    getAllNurseCarePlansByNcgId: async <T>(params: GetAllNurseCarePlansByNcgIdParamsDto): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/NurseCare/getAllNurseCarePlansByNcgId`, { params });
        return response.data;
      } catch (error) {
        console.error("Error fetching nurse care plans by NCG ID:", error);
        throw error;
      }
    },
    getNurseCarePlanDetailsById: async <T>(params: GetAllNurseCarePlanDetailsByIdParamsDto): Promise<T> => {
      try {
        console.log("evet bura")
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/NurseCarePlanDetail/getNurseCarePlanDetailById`, { params });
        return response.data;
      } catch (error) {
        console.error("Error fetching nurse care plan details by NCP ID:", error);
        throw error;
      }
    },
    getAllNurseCarePlanDetailsByNcpId: async <T>(params: GetAllNurseCarePlanDetailsByNcpIdParamsDto): Promise<T> => {
      try {
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/NurseCare/getAllNurseCarePlanDetailsByNcpId`, { params });
        return response.data;
      } catch (error) {
        console.error("Error fetching nurse care plan details by NCP ID:", error);
        throw error;
      }
    },
    createNurseCareGroup: async <T>(data: CreateNurseCareGroupRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/NurseCare/createNurseCareGroup`, data);
        return response.data;
      } catch (error) {
        console.error("Error creating nurse care group:", error);
        throw error;
      }
    },
    deleteNurseCareGroup: async <T>(params: DeleteNurseCareGroupPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/NurseCare/deleteNurseCareGroup${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting nurse care group:", error);
        throw error;
      }
    },
    updateNurseCareGroup: async <T>(params: UpdateNurseCareGroupPathParams, data: UpdateNurseCareGroupRequestDto): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/NurseCare/updateNurseCareGroup/${id}`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating nurse care group:", error);
        throw error;
      }
    },
  },

  // /api/NurseCarePlan
  nurseCarePlan: {
    create: async <T>(data: CreateNurseCarePlanRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/NurseCarePlan/create`, data);
        return response.data;
      } catch (error) {
        console.error("Error creating nurse care plan:", error);
        throw error;
      }
    },
    delete: async <T>(params: DeleteNurseCarePlanPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/NurseCarePlan/delete/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting nurse care plan:", error);
        throw error;
      }
    },
    update: async <T>(params: UpdateNurseCarePlanPathParams, data: UpdateNurseCarePlanRequestDto): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/NurseCarePlan/update/${id}`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating nurse care plan:", error);
        throw error;
      }
    },
  },

  // /api/NurseCarePlanDetail
  nurseCarePlanDetail: {
    create: async <T>(data: CreateNurseCarePlanDetailRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/NurseCarePlanDetail/create`, data);
        return response.data;
      } catch (error) {
        console.error("Error creating nurse care plan detail:", error);
        throw error;
      }
    },
    delete: async <T>(params: DeleteNurseCarePlanDetailPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/NurseCarePlanDetail/delete/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting nurse care plan detail:", error);
        throw error;
      }
    },
    update: async <T>(params: UpdateNurseCarePlanDetailPathParams, data: UpdateNurseCarePlanDetailRequestDto): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/NurseCarePlanDetail/update/${id}`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating nurse care plan detail:", error);
        throw error;
      }
    },
  },

  // /api/Patient
  patient: {
    getPatientInfo: async <T>(params: GetPatientInfoPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/Patient/GetPatientInfo/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching patient info:", error);
        throw error;
      }
    },
    getPatientVitalRecords: async <T>(params: { ptId: number; filterType?: string }): Promise<T> => {
      try {
        const { ptId, filterType = "All" } = params;
        const response = await axiosInstance.get<T>(
          `${API_URL}/nurse/api/Patient/GetPatientVitalRecords/${ptId}`,
          { params: { filterType } }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching patient vital records:", error);
        throw error;
      }
    },
    getPatientChartSummary: async (
      params: GetPatientChartSummaryParamsDto
    ): Promise<PatientChartSummaryData> => {
      try {
        const response = await axiosInstance.get<PatientChartSummaryResponseDto>(
          `${API_URL}/nurse/api/PatientChart/Summary`,
          { params }
        );

        const payload = response.data;
        const success =
          payload?.issuccess === true ||
          payload?.isSuccess === true ||
          payload?.statuscode === 200 ||
          payload?.statusCode === 200;

        if (!success) {
          throw new Error(payload?.message || "Failed to fetch patient chart summary");
        }

        return parsePatientChartSummaryData(payload?.data);
      } catch (error) {
        console.error("Error fetching patient chart summary:", error);
        throw error;
      }
    },
  },

  // /api/PatientNurseCarePlan
  patientNurseCarePlan: {
    createPatientNurseCarePlan: async <T>(data: CreatePatientNurseCarePlanRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/CreatePatientNurseCarePlan`, data);
        return response.data;
      } catch (error) {
        console.error("Error creating patient nurse care plan:", error);
        throw error;
      }
    },
    deletePatientNurseCarePlan: async <T>(params: DeletePatientNurseCarePlanPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/DeletePatientNurseCarePlan/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting patient nurse care plan:", error);
        throw error;
      }
    },
    deletePatientNurseCarePlanDetail: async <T>(params: DeletePatientNurseCarePlanDetailPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/DeletePatientNurseCarePlanDetail/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting patient nurse care plan detail:", error);
        throw error;
      }
    },
    updatePatientNurseCarePlanDetail: async <T>(data: UpdatePatientNurseCareDetailRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/UpdatePatientNurseCarePlanDetail`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating patient nurse care plan detail:", error);
        throw error;
      }
    },
    updatePatientNurseCarePlan: async <T>(data: UpdatePatientNurseCareRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/UpdatePatientNurseCarePlan`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating patient nurse care plan:", error);
        throw error;
      }
    },
    updatePatientNurseCarePlanDetailPeriod: async <T>(data: UpdatePatientNurseCareDetailPeriodRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/UpdatePatientNurseCarePlanDetailPeriod`, data);
        return response.data;
      } catch (error) {
        console.error("Error updating patient nurse care plan detail period:", error);
        throw error;
      }
    },
    getPatientNurseCarePlan: async <T>(params: GetPatientNurseCarePlanParamsDto): Promise<T> => {
      try {
        const { patientId, date } = params;
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/PatientNurseCarePlan/GetPatientNurseCarePlan/${patientId}`, { params: { date } });
        return response.data;
      } catch (error) {
        console.error("Error fetching patient nurse care plan:", error);
        throw error;
      }
    },
  },

  // /api/FavoriteNurseCareGroup
  favoriteNurseCareGroup: {
    createFavoriteNurseCareGroup: async <T>(data: CreateFavoriteNurseCareGroupRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/FavoriteNurseCareGroup/createFavoriteNurseCareGroup`, data);
        return response.data;
      } catch (error) {
        console.error("Error creating favorite nurse care group:", error);
        throw error;
      }
    },
    deleteFavoriteNurseCareGroup: async <T>(params: DeleteFavoriteNurseCareGroupPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/FavoriteNurseCareGroup/deleteFavoriteNurseCareGroup/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting favorite nurse care group:", error);
        throw error;
      }
    },
    getFavoriteNurseCareGroupsByUserId: async <T>(params: GetFavoriteNurseCareGroupsByUserIdParamsDto): Promise<T> => {
      try {
        const { userId } = params;
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/FavoriteNurseCareGroup/getFavoriteNurseCareGroupsByUserId/${userId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching favorite nurse care groups by user ID:", error);
        throw error;
      }
    },
  },

  // /api/FavoriteNurseCarePlan
  favoriteNurseCarePlan: {
    createFavoriteNurseCarePlan: async <T>(data: CreateFavoriteNurseCarePlanRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/FavoriteNurseCarePlan/createFavoriteNurseCarePlan`, data);
        return response.data;
      } catch (error) {
        console.error("Error creating favorite nurse care plan:", error);
        throw error;
      }
    },
    deleteFavoriteNurseCarePlan: async <T>(params: DeleteFavoriteNurseCarePlanPathParams): Promise<T> => {
      try {
        const { id } = params;
        const response = await axiosInstance.delete<T>(`${API_URL}/nurse/api/FavoriteNurseCarePlan/deleteFavoriteNurseCarePlan/${id}`);
        return response.data;
      } catch (error) {
        console.error("Error deleting favorite nurse care plan:", error);
        throw error;
      }
    },
    getFavoriteNurseCarePlansByUserId: async <T>(params: GetFavoriteNurseCarePlansByUserIdParamsDto): Promise<T> => {
      try {
        const { userId } = params;
        const response = await axiosInstance.get<T>(`${API_URL}/nurse/api/FavoriteNurseCarePlan/getFavoriteNurseCarePlansByUserId/${userId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching favorite nurse care plans by user ID:", error);
        throw error;
      }
    },
  },

  // /api/Order
  order: {
    authenForPatient: async <T>(data: AuthenForPatientRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/AuthenForPatient`, data);
        return response.data;
      } catch (error) {
        console.error("Error authenticating for patient:", error);
        throw error;
      }
    },
    verifyOrder: async <T>(data: VerifyOrderRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.put<T>(`${API_URL}/nurse/api/Order/VerifyOrder`, data);
        return response.data;
      } catch (error) {
        console.error("Error verifying order:", error);
        throw error;
      }
    },
    approveSendBackAsync: async <T>(data: ApproveSendBackRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/ApproveSendBackAsync`, data);
        return response.data;
      } catch (error) {
        console.error("Error sending back for approval:", error);
        throw error;
      }
    },
    orderAppDateChange: async <T>(data: OrderAppDateChangeRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/OrderAppDateChange`, data);
        return response.data;
      } catch (error) {
        console.error("Error changing order application date:", error);
        throw error;
      }
    },
    setStatusOtherTreatments: async <T>(data: SetStatusOtherTreatmentsRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/SetStatusOtherTreatments`, data);
        return response.data;
      } catch (error) {
        console.error("Error setting status for other treatments:", error);
        throw error;
      }
    },
    applyOrder: async <T>(data: ApplyOrderRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/ApplyOrder`, data);
        return response.data;
      } catch (error) {
        console.error("Error applying order:", error);
        throw error;
      }
    },
    notApplicable: async <T>(data: NotApplicableRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/NotApplicable`, data);
        return response.data;
      } catch (error) {
        console.error("Error marking order as not applicable:", error);
        throw error;
      }
    },
    undoApplyOrder: async <T>(data: { srumid: number }): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/UndoApplyOrder`, data);
        return response.data;
      } catch (error) {
        console.error("Error undoing order application:", error);
        throw error;
      }
    },
    cancelOrderTermination: async <T>(params: CancelOrderTerminationRequestDto): Promise<T> => {
      try {
        const response = await axiosInstance.post<T>(`${API_URL}/nurse/api/Order/CancelOrderTermination`, null, { params });
        return response.data;
      } catch (error) {
        console.error("Error cancelling order termination:", error);
        throw error;
      }
    },
  },
};

export default nurseService;
